import React from 'react';
import {connect} from 'react-redux';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {Grid, Header, Icon} from 'semantic-ui-react';
import _ from 'lodash';
import {
  safeMult,
  showDollarAmountWithDollar,
  getFirstErrorForField,
  safeAdd,
} from 'common/utils';
import {REQUEST_DETAILS_VALUE_CHANGE_DISPLAY} from 'app-ui-componentlibrary/dist/common/constants';
import {ContentDispatch} from 'framework';
import {
  COST_ITEM_PERIOD_TYPE,
  MODES,
  YEAR,
  DESCRIPTION,
  ADDITIONAL_INFORMATION,
  CARD_TYPES,
  FIELD_MAX_LENGTHS_SYSTEM,
  PRICE,
  QUANTITY,
  TOTAL,
  ACTUAL_PRICE,
  ACTUAL_QUANTITY,
  ACTUAL_TOTAL,
  NOT_FULLY_IN_SCOPE,
  SCOPE_OF_WORK_TOTAL,
  ALLOWED_EXCESS_TOTAL,
  DISALLOWED_TOTAL,
  CATEGORIZED_SUM,
} from 'common/requestDetails/constants';
import {
  extractChangeParamsFromCfg,
  createChangeObj,
  getErrorForField,
} from 'common/requestDetails/utils';
import HeaderSection from 'go/components/modules/requestDetails/v3/items/shared/simple/HeaderSection';
import produce from 'immer';
import DynamicElement from 'go/components/modules/requestDetails/v3/items/shared/DynamicElement';
import {ExpandingTextArea} from 'go/components/modules/requestDetails/v3/items/shared';
import {ReadOnlyDollarSignField} from 'app-ui-componentlibrary';
import {Contexts, Alert} from 'componentLibrary';

const {RequestDetailsContext} = Contexts;

const toError = (
  firstError,
  validationDirtyMap,
  validationDirtyPath,
  validationDirtyPage
) => {
  let showError = !_.get(firstError, 'isValid', true);
  const message = _.get(firstError, 'message', '');
  showError =
    showError &&
    (!validationDirtyPath ||
      _.get(validationDirtyMap, validationDirtyPath) ||
      validationDirtyPage);
  return {
    displayError: showError,
    errorMessages: message,
    notValid: !_.get(firstError, 'isValid', true),
  };
};

const FIELD_LENGTHS = _.get(FIELD_MAX_LENGTHS_SYSTEM, CARD_TYPES.SIMPLE);
const DESCRIPTION_MAX = _.get(FIELD_LENGTHS, DESCRIPTION);

export const displayError = (
  fieldName,
  validationDirtyPath,
  validationErrors,
  validationDirtyMap,
  validationDirtyPage
) => {
  // search through errors for the one about this field
  const firstError = getFirstErrorForField(_.get(validationErrors, 'error'), fieldName);
  return toError(
    firstError,
    validationDirtyMap,
    validationDirtyPath,
    validationDirtyPage
  );
};

export class SimpleInternal extends React.Component {
  constructor(props, context) {
    super(props, context);
    const {item, period, mode} = props;
    let costs =
      _.size(_.get(item, 'cost')) > period.increments
        ? _.slice(_.get(item, 'cost'), 0, period.increments)
        : _.get(item, 'cost', []);
    if (_.size(costs) < period.increments) {
      costs = [...costs, ..._.times(period.increments - _.size(costs), () => [])];
    }

    if (!_.isEqual(costs, _.get(item, 'cost')) && mode !== MODES.PAYMENT_REQUEST) {
      this.props
        .handleChange({
          ...item,
          cost: costs,
        })
        .then((res) => {
          this.props.handleBlur();
        });
    }
  }
  static contextType = RequestDetailsContext;

  /*
   * Check for changes on this item's level and (re)generate the "changes" array.
   */
  componentDidUpdate = (prevProps) => {
    const currPropsItem = this.props.currPropsItem;
    const prevPropsItem = prevProps.currPropsItem;
    const mode = _.get(this.context, 'mode');
    const showAdditionalQuestions = _.get(this.context, 'showAdditionalQuestions');
    const isReviewMode = mode === MODES.REVIEW;
    const prevItemDiffersFromCurrItem =
      !_.isEqual(
        _.get(prevPropsItem, 'additionalInformation'),
        _.get(currPropsItem, 'additionalInformation')
      ) && !_.get(currPropsItem, 'additionalInformation.ineligible', false);
    if (isReviewMode && showAdditionalQuestions && prevItemDiffersFromCurrItem) {
      this.handleFieldChanges(prevPropsItem, currPropsItem);
    }
    if (this.props.currentlyCollaborating) {
      this.props.handleChange(
        currPropsItem,
        prevPropsItem,
        this.props.currentlyCollaborating
      );
    }
  };

  handleFieldChanges = (prevItem, newItem) => {
    let {originalItem: oldItem, sourcePath, itemConfig} = this.props;
    const entityType = _.get(this.context, 'entityType');
    const saveItem = _.get(this.context, 'saveItem');
    let reviewContents = _.get(itemConfig, 'additionalQuestions.reviewContents', []);
    if (entityType == 'amendment')
      reviewContents = _.get(itemConfig, 'additionalQuestions.amendmentContents', []);
    const changeParams = extractChangeParamsFromCfg(reviewContents);

    // changes coming from props, so need to get mutable copy
    let changes = _.get(newItem, 'additionalInformation.changes');
    changes = changes ? _.cloneDeep(changes) : [];
    let fieldThatCausedChange;

    const newlyCreated = _.find(
      _.get(newItem, 'additionalInformation.changes', []),
      (change) => change.type === 'newlyCreated'
    );

    _.forEach(changeParams, (changeParam) => {
      const {fieldName, fieldPath, displayText, changeType} = changeParam;
      const oldFieldVal = _.get(oldItem, fieldPath);
      const newFieldVal = _.get(newItem, fieldPath);
      const prevFieldVal = _.get(prevItem, fieldPath);
      if (oldItem) {
        if (!_.isEqual(oldFieldVal, newFieldVal)) {
          const changeObj = _.find(changes, (o) =>
            _.isEqual(_.get(o, 'field'), fieldName)
          );
          // Only add field changes if not a newly created item (award rec compared to application, amendment compared to grant)
          if (!newlyCreated) {
            if (changeObj) {
              _.set(changeObj, 'currValue', newFieldVal);
              _.set(changeObj, 'previousValue', oldFieldVal);
            } else {
              changes.push(
                createChangeObj(
                  changeType,
                  fieldName,
                  oldFieldVal,
                  newFieldVal,
                  displayText,
                  this.props.currentlyCollaborating
                )
              );
            }
          }
          if (!_.isEqual(newFieldVal, prevFieldVal)) fieldThatCausedChange = fieldName;
        } else if (!_.isEqual(newFieldVal, prevFieldVal)) {
          // changed field reverted back to old value, remove its change object
          fieldThatCausedChange = fieldName;
          _.remove(changes, (o) => _.isEqual(_.get(o, 'field'), fieldName));
        }
      } else {
        // we're iterating through fields of an added item, don't touch it's change object, save field that changed
        if (!_.isEqual(newFieldVal, prevFieldVal)) fieldThatCausedChange = fieldName;
      }
    });
    let updatedItem = produce(newItem, (draft) => {
      if (_.isEmpty(changes)) {
        _.unset(draft, ['additionalInformation', 'justification']);
      }
      _.set(draft, ['additionalInformation', 'changes'], changes);
    });
    if (fieldThatCausedChange) {
      saveItem(sourcePath, updatedItem, fieldThatCausedChange);
      if (!this.props.currentlyCollaborating) {
        this.props.handleChange(updatedItem);
      }
    }
  };

  addCost = () => {
    let item = this.props.item;
    const newCost = produce(item.cost, (draft) => {
      draft[0].push({total: 0, quantity: 0, boc: '', price: ''});
    });
    this.props.handleItemChange('cost', newCost);
  };

  removeCost = (ind) => {
    let item = this.props.item;
    const newCost = produce(item.cost, (draft) => {
      _.pullAt(draft[0], ind);
    });
    this.props.handleItemChangeAndBlur('cost', newCost, `cost[0][${ind}].boc`);
  };

  retrieveLayoutGrid = (mode, originalItem) => {
    let layoutGrid = 'itemCardContents.detailSection.grid';
    if (mode === MODES.CLOSEOUT_REPORT) {
      layoutGrid = 'itemCardContents.detailSection.closeoutGrid';
      if (!originalItem) {
        layoutGrid = 'itemCardContents.detailSection.closeoutNewItemGrid';
      }
    } else if (mode === MODES.CLOSEOUT_REPORT_REVIEW) {
      layoutGrid = 'itemCardContents.detailSection.closeoutReviewGridReadOnly';
      const notFullyInScope = _.get(
        this.props.item,
        ADDITIONAL_INFORMATION + '.' + NOT_FULLY_IN_SCOPE,
        false
      );
      if (notFullyInScope) {
        layoutGrid = 'itemCardContents.detailSection.closeoutReviewGrid';
      }
    }
    return layoutGrid;
  };

  retrieveHeaderSection = (mode, originalItem) => {
    let headerSection = 'itemCardContents.headerSection';
    if (mode === MODES.CLOSEOUT_REPORT) {
      headerSection = 'itemCardContents.closeoutHeaderSection';
      if (!originalItem) {
        headerSection = 'itemCardContents.headerSection';
      }
    } else if (mode === MODES.CLOSEOUT_REPORT_REVIEW) {
      headerSection = 'itemCardContents.closeoutReviewHeaderSection';
    } else if (mode === MODES.PAYMENT_REQUEST) {
      if (!originalItem) {
        headerSection = 'itemCardContents.paymentRequestNewItemHeaderSection';
      }
    }
    return headerSection;
  };

  renderCommonCard = (conf, cost, index, costItemType, key) => {
    const {itemConfig, mode} = this.props;
    const labels = _.get(itemConfig, 'labels');
    const applicationGuidanceLabel = _.get(conf, 'applicationGuidanceLabel');
    const applicationGuidance = _.get(labels, applicationGuidanceLabel, undefined);
    const editable = this.props.editable;
    return (
      <>
        {mode === MODES.APPLICATION && applicationGuidance ? (
          <p>{applicationGuidance}</p>
        ) : null}
        <DynamicElement
          key={this.props.id + '_dyn_' + key}
          id={this.props.id}
          conf={conf}
          index={index}
          cost={cost}
          costItemType={costItemType}
          handleBlur={this.props.handleBlur}
          handleItemChange={this.props.handleItemChange}
          validationErrors={this.props.validationErrors}
          validationDirtyMap={this.props.validationDirtyMap}
          editable={editable}
        />
      </>
    );
  };

  renderWhole = (costs) => {
    const {
      itemConfig,
      editable,
      beingCreated,
      mode,
      originalItem,
      costSectionHeader,
    } = this.props;
    const closeoutMode =
      mode === MODES.CLOSEOUT_REPORT || mode === MODES.CLOSEOUT_REPORT_REVIEW;
    const paymentRequestMode = mode === MODES.PAYMENT_REQUEST;
    const maxCosts = _.get(itemConfig, 'maxCosts');
    const groupByRows = _.chain(
      _.get(itemConfig, this.retrieveLayoutGrid(mode, originalItem))
    )
      .groupBy('rowOrder')
      .map((value, key) => value)
      .value();
    const labels = _.get(itemConfig, 'labels');
    const applicationGuidanceLabel = _.get(
      itemConfig,
      'itemCardContents.detailSection.applicationGuidanceLabel'
    );
    const applicationGuidance = _.get(labels, applicationGuidanceLabel, undefined);
    const headerSection = this.retrieveHeaderSection(mode, originalItem);
    return (
      <>
        {paymentRequestMode && this.renderNonCostItemSection(headerSection)}
        {costs &&
          _.map(_.take(costs[0], maxCosts), (cost, ind) => {
            return (
              <React.Fragment>
                {maxCosts && (
                  <p className="padding-top">
                    <strong>Cost</strong> {ind + 1}
                  </p>
                )}
                {!paymentRequestMode && this.renderNonCostItemSection(headerSection)}
                {mode === MODES.APPLICATION && applicationGuidance ? (
                  <p>{applicationGuidance}</p>
                ) : null}
                {mode === MODES.PAYMENT_REQUEST && !!originalItem ? (
                  <h5>{costSectionHeader ? costSectionHeader : 'Grant amount'}</h5>
                ) : null}
                {_.map(groupByRows, (data, rowIndex) => {
                  let key = ind + '_' + rowIndex;
                  return (
                    <div
                      key={this.props.id + '_div_' + key}
                      className="app-form-row margin-bottom-25"
                    >
                      {_.map(data, (conf) => {
                        return this.renderCommonCard(
                          conf,
                          cost,
                          ind,
                          COST_ITEM_PERIOD_TYPE.WHOLE,
                          key
                        );
                      })}
                      {editable &&
                        !closeoutMode &&
                        maxCosts &&
                        !beingCreated &&
                        rowIndex + 1 == JSON.stringify(groupByRows.length) && (
                          <div className="margin-top30">
                            {
                              <a
                                id="delete-cost"
                                href="#"
                                onClick={() => this.removeCost(ind)}
                                title={`Click to remove cost`}
                              >
                                <Icon link name="close" color="red" />
                                Remove
                              </a>
                            }
                          </div>
                        )}
                    </div>
                  );
                })}
                {this.renderNonCostItemSection('itemCardContents.footerSection')}
              </React.Fragment>
            );
          })}
      </>
    );
  };

  getCostsTotalQuantity = (costs, attribute) => {
    let total = 0;
    _.map(costs, (costArray, ind) => {
      const value = parseFloat(_.get(costArray[0], attribute));
      if (!isNaN(value)) total = safeAdd(total, value);
    });
    if (total === 0) return '';
    return total;
  };

  getCostsDollarTotal = (costs) => {
    let total = this.getCostsTotalQuantity(costs, TOTAL);
    if (_.isNumber(total)) {
      let valueString = total.toString();
      return showDollarAmountWithDollar(valueString);
    }

    return total;
  };

  renderTotalsSection = (mode, costs, originalItem) => {
    let totalCostsErrObjs = getErrorForField(this.props.validationErrors, 'totalCosts');
    const totalCostsErrObj = totalCostsErrObjs ? totalCostsErrObjs[0] : undefined;
    let alertComponent = null;
    if (totalCostsErrObj && !totalCostsErrObj.isValid) {
      alertComponent = (
        <Alert header="Item total error" type="error">
          <strong>{totalCostsErrObj.message}</strong>
        </Alert>
      );
    }
    if (
      mode !== MODES.CLOSEOUT_REPORT &&
      mode !== MODES.CLOSEOUT_REPORT_REVIEW &&
      !(mode === MODES.PAYMENT_REQUEST && !originalItem)
    ) {
      return (
        <React.Fragment>
          <div className="app-form-row margin-bottom-25">
            <div className="app-form-block _20">{'TOTAL'}</div>
            <div className="app-form-block _20" style={{textAlign: 'right'}}>
              {costs ? this.getCostsTotalQuantity(costs, QUANTITY) : null}
            </div>
            <div className="app-form-block _20"> </div>
            <div className="app-form-block unit_total" style={{textAlign: 'right'}}>
              {costs ? this.getCostsDollarTotal(costs) : null}
            </div>
          </div>
          {alertComponent}
        </React.Fragment>
      );
    } else {
      return <React.Fragment> </React.Fragment>;
    }
  };

  renderNonCostItemSection = (section) => {
    const {itemConfig, item, mode} = this.props;
    const configSection = _.get(itemConfig, section);
    return (
      <React.Fragment>
        {_.map(configSection, (data, rowIndex) => {
          return this.renderCommonCard(data, item, rowIndex, undefined, rowIndex);
        })}
      </React.Fragment>
    );
  };

  renderYearLineItems = (costs) => {
    const {itemConfig, mode, originalItem, costSectionHeader} = this.props;

    const groupByRows = _.chain(
      _.get(itemConfig, this.retrieveLayoutGrid(mode, originalItem))
    )
      .groupBy('rowOrder')
      .map((value, key) => value)
      .value();
    const labels = _.get(itemConfig, 'labels');
    const applicationGuidanceLabel = _.get(
      itemConfig,
      'itemCardContents.detailSection.applicationGuidanceLabel'
    );
    const applicationGuidance = _.get(labels, applicationGuidanceLabel, undefined);
    return (
      <>
        {mode === MODES.APPLICATION && applicationGuidance ? (
          <p>{applicationGuidance}</p>
        ) : null}
        {mode === MODES.PAYMENT_REQUEST && !!originalItem ? (
          <h5>{costSectionHeader ? costSectionHeader : 'Grant amount'}</h5>
        ) : null}
        {costs &&
          _.map(costs, (costarray, ind) => {
            let cost = costarray[0];
            return (
              <React.Fragment>
                {_.map(groupByRows, (data, rowIndex) => {
                  let key = ind + '_' + rowIndex;
                  return (
                    <div key={rowIndex} className="app-form-row margin-bottom-25">
                      <div className="app-form-block _20">
                        <div className="questionText">
                          {REQUEST_DETAILS_VALUE_CHANGE_DISPLAY[YEAR]}
                        </div>
                        <div style={{textAlign: 'left'}}>{ind + 1}</div>
                      </div>
                      {_.map(data, (conf) => {
                        return this.renderCommonCard(
                          conf,
                          cost,
                          ind,
                          COST_ITEM_PERIOD_TYPE.YEAR,
                          key
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
      </>
    );
  };

  renderYear = (costs) => {
    const {mode, originalItem} = this.props;
    const headerSection = this.retrieveHeaderSection(mode, originalItem);
    return (
      <React.Fragment>
        {this.renderNonCostItemSection(headerSection)}
        {this.renderYearLineItems(costs)}
        {this.renderTotalsSection(mode, costs, originalItem)}
        {this.renderNonCostItemSection('itemCardContents.footerSection')}
      </React.Fragment>
    );
  };

  renderInternalData = (expanded) => {
    const {item: lineItem, period, mode} = this.props;
    const closeoutMode =
      mode === MODES.CLOSEOUT_REPORT || mode === MODES.CLOSEOUT_REPORT_REVIEW;
    period.increments = 6;
    let costs = _.get(lineItem, 'cost');
    let updatedCosts = _.cloneDeep(costs);
    costs = this.setTotalCostValues(costs, mode, expanded);
    updatedCosts = this.setTotalCostValues(updatedCosts, mode, expanded);
    const renderBasedOnPeriodType = (type, costs) => {
      switch (type) {
        case 'whole':
          return this.renderWhole(costs);
        case 'year':
          return this.renderYear(costs);
        case 'month':
        default:
          return null;
      }
    };
    // render the appropriate widget based on period type, only one period type implemented so far
    return (
      <React.Fragment>
        {renderBasedOnPeriodType(_.lowerCase(period.type), updatedCosts)}
      </React.Fragment>
    );
  };

  setTotalCostValues(costs, mode, expanded) {
    const closeoutMode =
      mode === MODES.CLOSEOUT_REPORT || mode === MODES.CLOSEOUT_REPORT_REVIEW;
    const closeoutReviewMode = mode === MODES.CLOSEOUT_REPORT_REVIEW;
    if (costs !== undefined) {
      _.forEach(costs, (cost, index) => {
        if (cost instanceof Array) {
          _.forEach(cost, (item, childIdx) => {
            if (closeoutMode) {
              if (expanded) {
                _.set(item, PRICE, 0);
                _.set(item, QUANTITY, 0);
              }
              _.set(
                item,
                ACTUAL_TOTAL,
                safeMult(_.get(item, ACTUAL_PRICE, 0), _.get(item, ACTUAL_QUANTITY, 0))
              );
              if (closeoutReviewMode) {
                _.set(
                  item,
                  CATEGORIZED_SUM,
                  safeAdd(
                    _.get(item, SCOPE_OF_WORK_TOTAL, 0),
                    _.get(item, ALLOWED_EXCESS_TOTAL, 0),
                    _.get(item, DISALLOWED_TOTAL, 0)
                  )
                );
              }
            }
            _.set(item, TOTAL, safeMult(_.get(item, PRICE, 0), _.get(item, QUANTITY, 0)));
          });
        } else {
          if (closeoutMode) {
            if (expanded) {
              _.set(cost, [index, PRICE], 0);
              _.set(cost, [index, QUANTITY], 0);
            }
            _.set(
              cost,
              [index, ACTUAL_TOTAL],
              safeMult(
                _.get(cost, [index, ACTUAL_PRICE], 0),
                _.get(cost, [index, ACTUAL_QUANTITY], 0)
              )
            );
            if (closeoutReviewMode) {
              _.set(
                item,
                CATEGORIZED_SUM,
                safeAdd(
                  _.get(item, [index, SCOPE_OF_WORK_TOTAL], 0),
                  _.get(item, [index, ALLOWED_EXCESS_TOTAL], 0),
                  _.get(item, {index, DISALLOWED_TOTAL}, 0)
                )
              );
            }
          }
          _.set(
            cost,
            [index, TOTAL],
            safeMult(_.get(cost, [index, PRICE], 0), _.get(cost, [index, QUANTITY], 0))
          );
        }
      });
    }
    _.forEach(costs, (cost) => {
      _.set(
        cost,
        [0, TOTAL],
        safeAdd(0, safeMult(_.get(cost, [0, PRICE], 0), _.get(cost, [0, QUANTITY], 0)))
      );
    });
    return costs;
  }

  renderDescription(expanded) {
    const {
      id,
      item: lineItem,
      handleBlur,
      validationErrors,
      validationDirtyMap,
      validationDirtyPage,
      mode,
    } = this.props;

    const descriptionPath = `${ADDITIONAL_INFORMATION}.${DESCRIPTION}`;
    const description = _.get(lineItem, descriptionPath);
    const descriptionLabel = REQUEST_DETAILS_VALUE_CHANGE_DISPLAY[DESCRIPTION];
    const descriptionErrors = displayError(
      DESCRIPTION,
      descriptionPath,
      validationErrors,
      validationDirtyMap,
      validationDirtyPage
    );
    return (
      <React.Fragment>
        <ExpandingTextArea
          id={id}
          fieldName={DESCRIPTION}
          expanded={expanded}
          value={description}
          label={descriptionLabel}
          closedLabel={descriptionLabel}
          handleChange={(value) =>
            this.props.handleItemChange('additionalInformation.description', value)
          }
          maxLength={DESCRIPTION_MAX}
          handleBlur={() => handleBlur(descriptionPath)}
          errorMessages={descriptionErrors.errorMessages}
          displayError={descriptionErrors.displayError}
        />
      </React.Fragment>
    );
  }

  isExpanded = () => {
    const {editable, originalItem, mode} = this.props;
    let closeoutMode = mode === MODES.CLOSEOUT_REPORT;
    let closeoutReportMode = mode === MODES.CLOSEOUT_REPORT_REVIEW;

    if (closeoutReportMode) {
      return false;
    }
    return editable && !(closeoutMode && originalItem);
  };

  getCloseoutReportReadOnlyContents = (editable, itemConfig) => {
    let contentsConfigPath = 'additionalQuestions.closeoutReportContentsReadOnly';
    if (
      editable &&
      _.has(itemConfig, 'additionalQuestions.closeoutReportContentsModification')
    ) {
      contentsConfigPath = 'additionalQuestions.closeoutReportContentsModification';
    }
    return contentsConfigPath;
  };

  renderAdditionalQuestions = () => {
    const {
      itemConfig,
      item: lineItem,
      period,
      rootPath,
      mode,
      editable,
      originalItem,
    } = this.props;
    const isReviewMode = mode === MODES.REVIEW || mode === MODES.CLOSEOUT_REPORT_REVIEW;
    const entityType = _.get(this.context, 'entityType');
    let contentsConfigPath =
      isReviewMode && editable
        ? 'additionalQuestions.reviewContents'
        : 'additionalQuestions.reviewContentsReadOnly';
    if (entityType == 'amendment')
      contentsConfigPath =
        isReviewMode && editable
          ? 'additionalQuestions.amendmentContents'
          : 'additionalQuestions.reviewContentsReadOnly';
    if (mode === MODES.CLOSEOUT_REPORT_REVIEW)
      contentsConfigPath = 'additionalQuestions.reviewContentsReadOnly';
    if (mode === MODES.CLOSEOUT_REPORT)
      contentsConfigPath =
        !originalItem && editable
          ? 'additionalQuestions.closeoutReportContents'
          : this.getCloseoutReportReadOnlyContents(editable, itemConfig);

    const additionalQuestionsFieldConfigs = _.get(itemConfig, contentsConfigPath);

    return (
      <Grid.Row>
        <Grid.Column width={8}>
          {_.map(additionalQuestionsFieldConfigs, (fieldConfig, index) => {
            let fieldConfigMap = {...fieldConfig};
            if (!_.isUndefined(this.props.additionalQuestionsEntityType)) {
              fieldConfigMap.entityType = this.props.additionalQuestionsEntityType;
            }
            return (
              <ContentDispatch
                key={fieldConfigMap.id || index}
                config={fieldConfigMap}
                rootPath={rootPath}
              />
            );
          })}
        </Grid.Column>
      </Grid.Row>
    );
  };

  render() {
    const {
      itemConfig,
      editable,
      beingCreated,
      item: lineItem,
      itemCodeOptions,
      rootPath,
      mode,
      displayFields,
      pastTotalPayment,
      originalItem,
    } = this.props;
    const {showAdditionalQuestions} = this.context;
    const reviewMode = mode === MODES.REVIEW;
    const renderAdditionalQuestionsContent =
      (mode === MODES.CLOSEOUT_REPORT && showAdditionalQuestions && !beingCreated) ||
      (mode === MODES.CLOSEOUT_REPORT_REVIEW && showAdditionalQuestions) ||
      (reviewMode &&
        showAdditionalQuestions &&
        (!editable || (editable && !beingCreated)));
    let costs = _.get(lineItem, 'cost[0]');
    let maxCosts = _.get(itemConfig, 'maxCosts');

    const itemCode = _.get(lineItem, 'itemCode');
    const itemName = _.get(itemConfig, 'name');

    const reviewOrCloseoutMode =
      mode === MODES.REVIEW ||
      mode === MODES.CLOSEOUT_REPORT ||
      mode === MODES.CLOSEOUT_REPORT_REVIEW;
    const applicationMode = mode === MODES.APPLICATION;
    const closeoutMode =
      mode === MODES.CLOSEOUT_REPORT || mode === MODES.CLOSEOUT_REPORT_REVIEW
        ? true
        : false;
    const paymentRequestMode = mode === MODES.PAYMENT_REQUEST;

    var tempItems = itemCodeOptions === undefined ? [] : [...itemCodeOptions];
    var index = _.findIndex(tempItems, function (o) {
      return o.key === String(itemCode);
    });
    const selected = {
      key: String(itemCode),
      value: String(itemCode),
      text: itemName,
    };
    if (index === -1) {
      tempItems.push(selected);
    }
    let expanded = this.isExpanded();

    return (
      <React.Fragment>
        {editable && (
          <HeaderSection
            {...this.props}
            itemCodeOptions={tempItems}
            expanded={expanded}
            handleItemChange={this.props.handleItemChange}
            handleItemChangeAndBlur={this.props.handleItemChangeAndBlur}
          />
        )}
        {_.includes(displayFields, 'description') && this.renderDescription(expanded)}
        {this.renderInternalData(expanded)}
        <div className="app-form-row margin-bottom-25">
          {maxCosts &&
            maxCosts > (costs ? _.size(costs) : 1) &&
            editable &&
            !beingCreated &&
            !closeoutMode && (
              <div>
                {
                  <a
                    id="add-cost"
                    href="#"
                    onClick={this.addCost}
                    title="Click to add an cost"
                  >
                    <Icon link name="add" color="green" />
                    Add cost
                  </a>
                }
              </div>
            )}
        </div>
        {!beingCreated && editable && applicationMode && (
          <Grid.Row>
            <Grid.Column width={8}>
              {_.map(_.get(itemConfig, 'additionalQuestions.contents'), (conf, index) => {
                let configMap = {...conf};
                if (!_.isUndefined(this.props.additionalQuestionsEntityType)) {
                  configMap.entityType = this.props.additionalQuestionsEntityType;
                }
                return (
                  <ContentDispatch
                    key={conf.id || index}
                    config={configMap}
                    rootPath={rootPath}
                  />
                );
              })}
            </Grid.Column>
          </Grid.Row>
        )}
        {renderAdditionalQuestionsContent && this.renderAdditionalQuestions()}
        {paymentRequestMode && !!originalItem ? (
          <div>
            <hr />
            <Header as="h5">Payment to date</Header>
            <Header as="h5">
              <ReadOnlyDollarSignField
                prependedText="Total "
                value={pastTotalPayment}
                alignLeft
                defaultValue=""
              />
            </Header>
          </div>
        ) : null}
      </React.Fragment>
    );
  }
}

export const mapStateToProps = (state, props) => {
  const {dynamicComponent} = state;
  const currPropsItem = _.get(dynamicComponent, props.rootPath);
  const currentlyCollaborating = !!_.get(
    state,
    'dynamicComponent.collaboration.roomList',
    ''
  );
  return {currPropsItem, currentlyCollaborating};
};

export default connect(mapStateToProps, null)(connectDynamic(SimpleInternal));
